import Tooltip from "components/Tooltip/Tooltip";
import { getLocalTimezoneDate } from "config/ts-utils";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import ReactTimeAgo from "timeago-react";

interface TimeAgoProps {
    className?: string;
    datetime: string;
    hideTooltip?: boolean;
}

/**
 * Throttle the auto-updating "X ago" label so it refreshes at most once per
 * minute instead of the library default of once per second for recently-changed
 * timestamps. `minInterval` (seconds) raises the floor on timeago.js's internal
 * timer; its natural coarsening for older times (≥1 min → minute cadence, etc.)
 * is unchanged. This eliminates the per-second DOM churn that dominates idle CPU
 * on dense live screens (the operator dashboard renders one TimeAgo per row plus
 * one per live widget), matching the refresh cadence to how fast a relative-time
 * label actually changes.
 */
const TIMEAGO_OPTS = { minInterval: 60 };

export const TimeAgo = ({ className, datetime, hideTooltip }: TimeAgoProps) => {
    const localTimezoneDate = getLocalTimezoneDate(datetime);

    return hideTooltip ? (
        <ReactTimeAgo className={className} datetime={datetime} opts={TIMEAGO_OPTS} />
    ) : (
        <Tooltip
            overlay={localTimezoneDate}
            overlayStyle={{ zIndex: 3000 }}
            overlayClassName="light overlapText"
            triggerClassName="overlapTextTrigger"
            placement="bottomLeft"
            align={{ points: ["cc", "cc"], offset: [0, 0] }}
        >
            <ReactTimeAgo className={className} datetime={datetime} opts={TIMEAGO_OPTS} />
        </Tooltip>
    );
};

export const TimeLeft = ({ datetime, hideTooltip }: TimeAgoProps) => {
    const localTimezoneDate = getLocalTimezoneDate(datetime);
    dayjs.extend(relativeTime);
    const time = dayjs(datetime).endOf("hour").fromNow();

    return hideTooltip ? (
        <span>{time}</span>
    ) : (
        <Tooltip
            overlay={localTimezoneDate}
            overlayStyle={{ zIndex: 3000 }}
            overlayClassName="light overlapText"
            triggerClassName="overlapTextTrigger"
            placement="bottomLeft"
            align={{ points: ["cc", "cc"], offset: [0, 0] }}
        >
            {time}
        </Tooltip>
    );
};
